import { useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Fieldset,
  Form,
  TextArea,
} from "@datapay-by-datacom/design-system";
import {
  useRetireCompanyConfig,
  useUnsavedProgressNavigationGuard,
} from "../hooks";
import { backendErrorMessage } from "../utils/backendErrorMessage";
import { UnsavedProgressConfirmModal } from "./UnsavedProgressConfirmModal";
import s from "./RetireCompanyConfigForm.module.css";

interface RetireCompanyConfigFormProps {
  configCode: string;
  version: number;
  presentation?: "legacy" | "redesign";
  configurationBasePath?: string;
}

/**
 * "Retire company configuration" form. Retiring requires a non-empty explanation
 * (`CompanyConfigRetire.change_description`, minLength 1). There is no
 * "retirement category" field in the API, so the concept's category dropdown
 * is intentionally omitted.
 */
export function RetireCompanyConfigForm({
  configCode,
  version,
  presentation = "redesign",
  configurationBasePath = "/onboarding",
}: RetireCompanyConfigFormProps) {
  const navigate = useNavigate();
  const retireMutation = useRetireCompanyConfig(configCode);
  const [explanation, setExplanation] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const isEmpty = !explanation.trim();
  const navigationGuard = useUnsavedProgressNavigationGuard(
    explanation.length > 0
  );
  const configurationPath = `${configurationBasePath}/${configCode}`;
  const backToCompanyConfig = () => navigate(configurationPath);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (isEmpty) {
      return;
    }

    retireMutation.mutate(
      { version, body: { change_description: explanation.trim() } },
      {
        onSuccess: () => {
          navigationGuard.bypassNextNavigation();
          navigate(configurationBasePath, {
            state: {
              statusMessage: `Company configuration ${configCode} version ${version} retired successfully.`,
            },
          });
        },
      }
    );
  };

  const showRequiredError = submitted && isEmpty;
  const isRedesign = presentation === "redesign";
  const formFields = (
    <>
      {isRedesign && (
        <div className={s.guidance} role="note">
          <p className={s.guidanceTitle}>About retiring this version</p>
          <p className={s.guidanceText}>
            Version {version} will no longer be active. If an earlier published
            version exists, it becomes the current version.
          </p>
        </div>
      )}

      <TextArea
        label="Explanation"
        isRequired
        rows={5}
        placeholder="Why is this company configuration being retired?"
        value={explanation}
        onChange={setExplanation}
        errorMessage={showRequiredError ? "Explanation is required." : ""}
        isInvalid={showRequiredError}
      />

      <ButtonGroup align="right">
        <Button
          variant={isRedesign ? "secondary" : "tertiary"}
          onPress={backToCompanyConfig}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          isDisabled={isEmpty || retireMutation.isPending}
        >
          {retireMutation.isPending ? "Retiring…" : "Confirm retire"}
        </Button>
      </ButtonGroup>
    </>
  );

  return (
    <div className={`${s.container} ${isRedesign ? s.redesignContainer : ""}`}>
      {isRedesign && (
        <Link to={configurationPath} className={s.backLink}>
          &larr; Back to configuration
        </Link>
      )}

      <header className={s.header}>
        <h1 className={s.title}>Retire company configuration</h1>
        <p className={s.subtitle}>
          {configCode} · v{version}
        </p>
      </header>

      {retireMutation.isError && (
        <div className={s.error} role="alert">
          {backendErrorMessage(
            retireMutation.error,
            "Could not retire this company configuration. Please try again."
          )}
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Fieldset>
          {isRedesign ? (
            <div className={s.redesignForm}>{formFields}</div>
          ) : (
            formFields
          )}
        </Fieldset>
      </Form>
      <UnsavedProgressConfirmModal
        isOpen={navigationGuard.isBlocked}
        onStay={navigationGuard.stay}
        onLeave={navigationGuard.leaveAnyway}
        title="Leave retirement page?"
        message="Your retirement explanation will be lost."
      />
    </div>
  );
}
